import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { ASSISTANT_RAG_LABELS_FR, RagAskLabels } from '../config/rag-ask.config';
import { AskSource } from '../models/ask.models';
import { RagPreviewService } from '../services/rag-preview.service';
import {
  formatScore,
  freshnessBadge,
  referenceDate,
  referenceDateLabel,
  scoreColor,
  FreshnessBadge,
} from '../util/source.utils';
import { RagIconComponent } from './rag-icon.component';

interface SourceRow {
  readonly src: AskSource;
  readonly rank: number;
  readonly title: string;
  readonly badge: FreshnessBadge;
  readonly date: string | null;
  readonly dateLabel: string;
  readonly score: number;
  readonly scoreColor: string;
}

@Component({
  selector: 'rag-ask-sources',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgFor, NgIf, RagIconComponent],
  template: `
    <div class="rag-sources" role="list" [attr.aria-label]="labels.sourcesLabel">
      <span class="rag-sources__label">{{ labels.sourcesLabel }}</span>

      <ng-container *ngFor="let row of rows; let i = index; trackBy: trackByRank">
        <div class="rag-source-row" role="listitem">
          <a class="rag-source" [href]="row.src.link" target="_blank" rel="noopener noreferrer">
            <span class="rag-source__rank">{{ row.rank }}</span>
            <span class="rag-source__title" [title]="row.title">{{ row.title }}</span>
            <span *ngIf="showFreshness || row.date || showScores" class="rag-source__meta">
              <span *ngIf="showFreshness" class="rag-badge" [class]="row.badge.modifier">
                <rag-icon [name]="row.badge.icon" [size]="13"></rag-icon>
                <span class="rag-badge__text">{{ row.badge.label }}</span>
              </span>
              <span *ngIf="row.date" class="rag-source__date">{{ row.dateLabel }} {{ row.date }}</span>
              <span *ngIf="showScores" class="rag-source__score" [style.color]="row.scoreColor"
                >{{ row.score }}&nbsp;%</span
              >
            </span>
            <rag-icon class="rag-source__external" name="external" [size]="13"></rag-icon>
          </a>

          <button
            #trigger
            type="button"
            class="rag-source__toggle"
            [class.rag-source__toggle--active]="isOpen(trigger)"
            [attr.aria-expanded]="isOpen(trigger)"
            [attr.aria-label]="isOpen(trigger) ? labels.hideExtract : labels.showExtract"
            [title]="isOpen(trigger) ? labels.hideExtract : labels.showExtract"
            (click)="onToggle($event, i)"
          >
            <rag-icon name="eye" [size]="16"></rag-icon>
          </button>
        </div>
      </ng-container>
    </div>
  `,
})
export class RagAskSourcesComponent {
  private readonly preview = inject(RagPreviewService, { optional: true });

  @Input({ required: true }) sources: readonly AskSource[] = [];
  @Input() labels: RagAskLabels = ASSISTANT_RAG_LABELS_FR;
  @Input() showScores = false;
  @Input() showFreshness = true;
  @Input() locale: string | undefined;

  protected get rows(): SourceRow[] {
    let format: Intl.DateTimeFormat;
    try {
      format = new Intl.DateTimeFormat(this.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      format = new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }
    return (this.sources ?? []).map((src, index) => {
      const raw = referenceDate(src);
      const parsed = raw ? new Date(raw) : null;
      return {
        src,
        rank: index + 1,
        title: src.title || src.source || src.link,
        badge: freshnessBadge(src.freshness, this.labels),
        date: parsed && !Number.isNaN(parsed.getTime()) ? format.format(parsed) : null,
        dateLabel: referenceDateLabel(src, this.labels),
        score: formatScore(src.score),
        scoreColor: scoreColor(src.score),
      };
    });
  }

  protected trackByRank(_i: number, row: SourceRow): number {
    return row.rank;
  }

  /**
   * True while the popover is open on this very button. Compared by element,
   * not by source index, so row 2 of another answer does not light up too.
   */
  protected isOpen(trigger: HTMLElement): boolean {
    return !!this.preview && this.preview.anchor() === trigger;
  }

  onToggle(event: Event, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    const marker = event.currentTarget as HTMLElement | null;
    if (!marker || !this.preview) return;
    this.preview.toggle(marker, this.sources, index);
  }
}
